import { Text } from '@chakra-ui/layout';
import type { SystemStyleObject } from '@chakra-ui/styled-system';
import type { ReactNode } from 'react';
import { Textfit } from 'react-textfit';
import type { CSSProperties } from 'styled-components';

import { useResizeObserver } from '@revelio/core';
import type {
  CustomRoleTaxonomySelection,
  GetEntityDataV3Query,
} from '@revelio/data-access';
import { Divider } from '@revelio/ui';

import type { RoleSelectionListKey } from '../../../../../shared/selection-lists/types';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { useRoleSelectionLists } from '../../../filters/selectionList/useRoleSelectionList';
import {
  FilterId,
  FilterType,
  type SingleValueFilter,
} from '../../../filters/types/filters';
import { PlotInfoComp } from '../PlotInfoComp';
import styles from './role-summary.module.css';

interface RoleSummaryProps {
  data: GetEntityDataV3Query;
  isDragging?: boolean;
}

const TEXT_STYLES: CSSProperties = {
  height: '90%',
  lineHeight: '1.2',
  fontWeight: 300,
  overflow: 'hidden',
};

export const RoleSummary = ({ data, isDragging }: RoleSummaryProps) => {
  const { containerRef, width, height } = useResizeObserver();
  const effectiveFilters = useEffectiveFilters();
  const primaryFilter = effectiveFilters.find(
    (filter) => filter.id === FilterId.ROLE
  );

  const roleTaxonomyFilter = effectiveFilters.find(
    (filter): filter is SingleValueFilter<CustomRoleTaxonomySelection> =>
      filter.id === FilterId.ROLE_TAXONOMY && filter.type === FilterType.VALUE
  );
  const isDefaultTaxonomy = roleTaxonomyFilter?.value?.default ?? false;

  const roleSelectionLists = useRoleSelectionLists();

  const selectedPrimarySelection =
    primaryFilter?.type === FilterType.TREE
      ? primaryFilter?.primarySelections?.[0]
      : undefined;

  const commonTitles: string[] = (() => {
    if (!selectedPrimarySelection) return [];

    const levelId = selectedPrimarySelection?.levelId as RoleSelectionListKey;
    const selectionList = roleSelectionLists?.[levelId];
    const selectedItem = selectionList?.find(
      (item) => item?.id === selectedPrimarySelection?.id?.split('_').pop()
    );

    if (selectedItem && selectedItem.rawTitles) return selectedItem.rawTitles;
    return [];
  })();

  const entity = data?.entityV3?.entity;
  const roleTitle = entity?.name;
  const description = entity?.description;
  const commonTitlesText = commonTitles.join(', ');
  const canFit = Boolean(width && height) && !isDragging;

  if (isDefaultTaxonomy) {
    return (
      <div className={styles.layout}>
        <div className={styles.titleRow} data-info-hover>
          <Text fontWeight={600} fontSize={14}>
            {roleTitle}
          </Text>
          <PlotInfoComp
            plotInfoConfig={{
              popoverPlacement: 'right',
              iconBoxSize: 2.5,
            }}
          >
            <Text>Descriptions are sourced from AI generated text.</Text>
          </PlotInfoComp>
        </div>
        <div className={styles.description} ref={containerRef}>
          <TextBlock
            content={description}
            style={TEXT_STYLES}
            canFit={canFit}
          />
        </div>

        <div className={styles.facts}>
          <Divider className={styles.factsDivider} />
          <div className={styles.titlesRow}>
            <Text
              fontSize={14}
              title={commonTitlesText}
              sx={{
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2, // Limits to 2 lines and adds ellipsis
              }}
            >
              <b>Common Titles:</b>{' '}
              <Text fontWeight={300} as="span">
                {commonTitlesText}
              </Text>
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <div className={styles.titleRow} role="group">
        <Text fontWeight={600} fontSize={14}>
          {selectedPrimarySelection?.name || roleTitle} - Common Titles
        </Text>
      </div>
      <div className={styles.textArea} ref={containerRef}>
        <TextBlock
          content={commonTitlesText}
          style={TEXT_STYLES}
          canFit={canFit}
        />
      </div>
    </div>
  );
};

const TextBlock = ({
  content,
  style,
  sx,
  canFit,
}: {
  content: ReactNode;
  style?: CSSProperties;
  sx?: SystemStyleObject;
  canFit?: boolean;
}) => {
  if (!canFit) {
    return (
      <Text sx={sx} style={style}>
        {content}
      </Text>
    );
  }
  return (
    <Textfit mode="multi" min={10} max={25} style={style}>
      <Text sx={sx}>{content}</Text>
    </Textfit>
  );
};
